import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { CommonService } from '../../common-service';
import {
  CriarInventarioResponse,
  GetInventarioByIdResponse,
  GetInventarioDigitacaoResponse,
  GetInventarioLookupResponse,
  GetInventarioResponse,
  GetProdutosEmInventarioAbertoResponse,
  SalvarContagemItemResponse,
} from '../../responses/suprimentos/inventario.response';
import { ReturnStruct } from '../../structs/return.struct';
import { CriarInventarioRequest } from '../../requests/suprimentos/criar-inventario.request';
import { CriarInventarioLocalizacaoRequest } from '../../requests/suprimentos/criar-inventario-localizacao.request';
import { SalvarContagemItemRequest } from '../../requests/suprimentos/salvar-contagem-item.request';
import { SalvarLotesInventarioItemRequest } from '../../requests/suprimentos/salvar-lotes-inventario-item.request';
import { SalvarSeriesInventarioItemRequest } from '../../requests/suprimentos/salvar-series-inventario-item.request';

@Injectable({ providedIn: 'root' })
export class InventarioService extends CommonService {
  private readonly apiUrl: string = environment.urlApiSuprimentos + 'Inventario';

  constructor(private httpClient: HttpClient) {
    super();
  }

  public getAll(
    idStatusInventario?: number | null,
    idTipoOrigemInventario?: number | null,
    descricao?: string,
    pagina: number = 1,
    tamanhoPagina: number = 20
  ): Observable<GetInventarioResponse> {
    let params = new HttpParams()
      .set('pagina', pagina.toString())
      .set('tamanhoPagina', tamanhoPagina.toString());

    if (idStatusInventario != null) params = params.set('idStatusInventario', idStatusInventario.toString());
    if (idTipoOrigemInventario != null) params = params.set('idTipoOrigemInventario', idTipoOrigemInventario.toString());
    if (descricao) params = params.set('descricao', descricao);

    return this.httpClient
      .get<GetInventarioResponse>(this.apiUrl, { params, ...this.addHeaderToken() })
      .pipe(catchError(this.handleError));
  }

  public getById(idInventario: number): Observable<GetInventarioByIdResponse> {
    return this.httpClient
      .get<GetInventarioByIdResponse>(`${this.apiUrl}/idInventario/${idInventario}`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public getLookup(): Observable<GetInventarioLookupResponse> {
    return this.httpClient
      .get<GetInventarioLookupResponse>(`${this.apiUrl}/lookup`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public criar(request: CriarInventarioRequest): Observable<CriarInventarioResponse> {
    return this.httpClient
      .post<CriarInventarioResponse>(this.apiUrl, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public atualizar(idInventario: number, request: CriarInventarioRequest): Observable<GetInventarioByIdResponse> {
    return this.httpClient
      .put<GetInventarioByIdResponse>(`${this.apiUrl}/idInventario/${idInventario}`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public getDigitacao(idInventario: number): Observable<GetInventarioDigitacaoResponse> {
    return this.httpClient
      .get<GetInventarioDigitacaoResponse>(`${this.apiUrl}/idInventario/${idInventario}/digitacao`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public salvarContagem(idInventario: number, idInventarioItem: number, request: SalvarContagemItemRequest): Observable<SalvarContagemItemResponse> {
    return this.httpClient
      .put<SalvarContagemItemResponse>(`${this.apiUrl}/idInventario/${idInventario}/item/${idInventarioItem}`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public salvarLotes(idInventario: number, idInventarioItem: number, request: SalvarLotesInventarioItemRequest): Observable<SalvarContagemItemResponse> {
    return this.httpClient
      .put<SalvarContagemItemResponse>(`${this.apiUrl}/idInventario/${idInventario}/item/${idInventarioItem}/lotes`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public salvarSeries(idInventario: number, idInventarioItem: number, request: SalvarSeriesInventarioItemRequest): Observable<SalvarContagemItemResponse> {
    return this.httpClient
      .put<SalvarContagemItemResponse>(`${this.apiUrl}/idInventario/${idInventario}/item/${idInventarioItem}/series`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Exclui um inventário (apenas status Pendente).
   * Corresponde a: [HttpDelete("idInventario/{idInventario}")]
   */
  public deletar(idInventario: number): Observable<ReturnStruct> {
    return this.httpClient
      .delete<ReturnStruct>(`${this.apiUrl}/idInventario/${idInventario}`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Produtos presentes em inventários abertos (Pendente/EmProcessamento/AguardandoProcessamento),
   * para a regra de conflito na geração/edição.
   * Corresponde a: [HttpGet("produtosEmAberto")]
   */
  public getProdutosEmAberto(idInventarioIgnorar?: number): Observable<GetProdutosEmInventarioAbertoResponse> {
    let params = new HttpParams();
    if (idInventarioIgnorar != null) params = params.set('idInventarioIgnorar', idInventarioIgnorar.toString());

    return this.httpClient
      .get<GetProdutosEmInventarioAbertoResponse>(`${this.apiUrl}/produtosEmAberto`, { params, ...this.addHeaderToken() })
      .pipe(catchError(this.handleError));
  }

  /**
   * Adiciona itens a um inventário pendente.
   * Corresponde a: [HttpPost("idInventario/{idInventario}/adicionar-itens")]
   */
  public adicionarItens(idInventario: number, localizacoes: CriarInventarioLocalizacaoRequest[]): Observable<ReturnStruct> {
    return this.httpClient
      .post<ReturnStruct>(`${this.apiUrl}/idInventario/${idInventario}/adicionar-itens`, localizacoes, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public cancelar(idInventario: number): Observable<GetInventarioByIdResponse> {
    return this.httpClient
      .post<GetInventarioByIdResponse>(`${this.apiUrl}/idInventario/${idInventario}/cancelar`, {}, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public concluir(idInventario: number): Observable<GetInventarioByIdResponse> {
    return this.httpClient
      .post<GetInventarioByIdResponse>(`${this.apiUrl}/idInventario/${idInventario}/concluir`, {}, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }
}
